import type {
  MapComponentFilterField,
  MapComponentFilterFieldValueType,
  MapComponentFilterOperator,
} from './types';

export type StyleRuleLogic = StyleRuleGroup | StyleRuleCondition;

export interface StyleRuleGroup {
  id: string;
  type: 'group';
  operator: 'AND' | 'OR';
  children: StyleRuleLogic[];
}

export interface StyleRuleCondition {
  id: string;
  type: 'condition';
  fieldId: string;
  operatorId: string;
  values: string[];
}

const randomId = (prefix: string) => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
};

export const createGroup = (overrides?: Partial<StyleRuleGroup>): StyleRuleGroup => ({
  id: overrides?.id ?? randomId('group'),
  type: 'group',
  operator: overrides?.operator ?? 'AND',
  children: overrides?.children ?? [],
});

export const createCondition = (fieldId: string, operatorId: string): StyleRuleCondition => ({
  id: randomId('condition'),
  type: 'condition',
  fieldId,
  operatorId,
  values: [],
});

export const ensureLogicRoot = (logic?: StyleRuleLogic | null): StyleRuleGroup => {
  if (!logic) {
    return createGroup();
  }
  if (logic.type === 'group') {
    return { ...logic, children: logic.children.map(child => (child.type === 'group' ? ensureLogicRoot(child) : child)) };
  }
  return createGroup({ children: [logic] });
};

export const countConditions = (logic: StyleRuleLogic): number => {
  if (logic.type === 'condition') {
    return 1;
  }
  return logic.children.reduce((acc, child) => acc + countConditions(child), 0);
};

type LogicMutator = (node: StyleRuleLogic) => StyleRuleLogic | null;

const updateNodeInternal = (node: StyleRuleLogic, id: string, mutator: LogicMutator): StyleRuleLogic | null => {
  if (node.id === id) {
    return mutator(node);
  }
  if (node.type !== 'group') {
    return node;
  }
  const children = node.children
    .map(child => updateNodeInternal(child, id, mutator))
    .filter((child): child is StyleRuleLogic => Boolean(child));
  return { ...node, children };
};

export const updateLogicNode = (
  logic: StyleRuleLogic,
  id: string,
  mutator: (node: StyleRuleLogic) => StyleRuleLogic | null,
): StyleRuleLogic => updateNodeInternal(logic, id, mutator) ?? logic;

export const appendChild = (logic: StyleRuleLogic, parentId: string, child: StyleRuleLogic): StyleRuleLogic => {
  if (logic.id === parentId && logic.type === 'group') {
    return { ...logic, children: [...logic.children, child] };
  }
  if (logic.type !== 'group') {
    return logic;
  }
  return {
    ...logic,
    children: logic.children.map(entry => appendChild(entry, parentId, child)),
  };
};

export const removeLogicNode = (logic: StyleRuleLogic, id: string): StyleRuleLogic => {
  if (logic.id === id) {
    return logic;
  }
  if (logic.type !== 'group') {
    return logic;
  }
  const children = logic.children
    .filter(child => child.id !== id)
    .map(child => removeLogicNode(child, id));
  return { ...logic, children };
};

const fallbackOperators: Record<MapComponentFilterFieldValueType, MapComponentFilterOperator[]> = {
  number: [
    { id: 'eq', label: 'Equals', input: 'text' },
    { id: 'gt', label: 'Greater than', input: 'text' },
    { id: 'lt', label: 'Less than', input: 'text' },
  ],
  boolean: [{ id: 'eq', label: 'Equals', input: 'select' }],
  date: [
    { id: 'eq', label: 'On', input: 'date' },
    { id: 'after', label: 'After', input: 'date' },
    { id: 'before', label: 'Before', input: 'date' },
  ],
  string: [
    { id: 'eq', label: 'Equals', input: 'text' },
    { id: 'contains', label: 'Contains', input: 'text' },
  ],
  unknown: [{ id: 'eq', label: 'Equals', input: 'text' }],
};

export const resolveFieldOperators = (field?: MapComponentFilterField): MapComponentFilterOperator[] => {
  if (!field) {
    return fallbackOperators.string;
  }
  if (field.operators && field.operators.length > 0) {
    return field.operators;
  }
  return fallbackOperators[field.value_type ?? 'string'] ?? fallbackOperators.string;
};

export type OperatorInputKind = 'none' | 'single' | 'multi' | 'range' | 'date' | 'select';

const operatorInputKind = (operator?: MapComponentFilterOperator): OperatorInputKind => {
  const input = operator?.input?.toLowerCase();
  switch (input) {
    case 'none':
      return 'none';
    case 'date':
      return 'date';
    case 'range':
      return 'range';
    case 'multi':
      return 'multi';
    case 'select':
      return 'select';
    case 'text':
    case 'single':
    default:
      return 'single';
  }
};

export const getOperatorById = (
  field: MapComponentFilterField | undefined,
  operatorId: string,
): MapComponentFilterOperator | undefined => resolveFieldOperators(field).find(op => op.id === operatorId);

export const getOperatorInput = (
  field: MapComponentFilterField | undefined,
  operatorId: string,
): OperatorInputKind => operatorInputKind(getOperatorById(field, operatorId));

const escapeCelString = (value: string): string => value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

const quote = (value: string): string => `'${escapeCelString(value)}'`;

const literalForValue = (value: string, valueType: MapComponentFilterFieldValueType): string => {
  if (valueType === 'number') {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return String(numeric);
    }
  }
  if (valueType === 'boolean') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(normalized)) {
      return 'true';
    }
    if (['false', '0', 'no', 'n'].includes(normalized)) {
      return 'false';
    }
  }
  return quote(value.trim());
};

const listLiteral = (values: string[], field: MapComponentFilterField): string => {
  if (!values.length) {
    return '[]';
  }
  return `[${values.map(value => literalForValue(value, field.value_type ?? 'string')).join(', ')}]`;
};

export const buildExpressionFromLogic = (logic: StyleRuleLogic, fields: MapComponentFilterField[]): string => {
  if (logic.type === 'condition') {
    if (logic.operatorId === 'match_everything') {
      return 'true';
    }
    const field = fields.find(entry => entry.id === logic.fieldId);
    if (!field) {
      return '';
    }
    const operator = getOperatorById(field, logic.operatorId);
    const operatorId = operator?.id ?? logic.operatorId;
    const fieldAccessor = `properties.${field.id}`;

    switch (operatorId) {
      case 'eq':
      case 'equals':
        return `${fieldAccessor} == ${literalForValue(logic.values[0] ?? '', field.value_type ?? 'string')}`;
      case 'neq':
      case 'not_equals':
        return `${fieldAccessor} != ${literalForValue(logic.values[0] ?? '', field.value_type ?? 'string')}`;
      case 'gt':
      case 'greaterthan':
        return `${fieldAccessor} > ${literalForValue(logic.values[0] ?? '', field.value_type ?? 'number')}`;
      case 'gte':
      case 'greaterthanorequal':
        return `${fieldAccessor} >= ${literalForValue(logic.values[0] ?? '', field.value_type ?? 'number')}`;
      case 'lt':
      case 'lessthan':
        return `${fieldAccessor} < ${literalForValue(logic.values[0] ?? '', field.value_type ?? 'number')}`;
      case 'lte':
      case 'lessthanorequal':
        return `${fieldAccessor} <= ${literalForValue(logic.values[0] ?? '', field.value_type ?? 'number')}`;
      case 'after':
        return `${fieldAccessor} > ${literalForValue(logic.values[0] ?? '', 'date')}`;
      case 'before':
        return `${fieldAccessor} < ${literalForValue(logic.values[0] ?? '', 'date')}`;
      case 'contains':
        return `(${fieldAccessor} ?? '').contains(${literalForValue(logic.values[0] ?? '', 'string')})`;
      case 'not_contains':
        return `!(${fieldAccessor} ?? '').contains(${literalForValue(logic.values[0] ?? '', 'string')})`;
      case 'starts_with':
        return `(${fieldAccessor} ?? '').startsWith(${literalForValue(logic.values[0] ?? '', 'string')})`;
      case 'ends_with':
        return `(${fieldAccessor} ?? '').endsWith(${literalForValue(logic.values[0] ?? '', 'string')})`;
      case 'in':
        return `${listLiteral(logic.values, field)}.exists(value, value == ${fieldAccessor})`;
      default:
        return `${fieldAccessor} == ${literalForValue(logic.values[0] ?? '', field.value_type ?? 'string')}`;
    }
  }

  const operator = logic.operator ?? 'AND';
  const joiner = operator === 'OR' ? ' || ' : ' && ';
  return logic.children.map(child => buildExpressionFromLogic(child, fields)).filter(Boolean).join(joiner);
};

export const restoreLogic = (logic: StyleRuleLogic | null | undefined): StyleRuleLogic | null => {
  if (!logic || typeof logic !== 'object') {
    return null;
  }
  if (logic.type === 'condition') {
    if (!logic.id) {
      return null;
    }
    if ((logic.operatorId ?? 'eq') !== 'match_everything' && !logic.fieldId) {
      return null;
    }
    return {
      id: logic.id,
      type: 'condition',
      fieldId: logic.fieldId,
      operatorId: logic.operatorId ?? 'eq',
      values: Array.isArray(logic.values) ? logic.values.map(value => String(value)) : [],
    };
  }
  if (logic.type === 'group') {
    const children = Array.isArray(logic.children)
      ? logic.children.map(entry => restoreLogic(entry)).filter(Boolean) as StyleRuleLogic[]
      : [];
    return {
      id: logic.id ?? randomId('group'),
      type: 'group',
      operator: logic.operator === 'OR' ? 'OR' : 'AND',
      children,
    };
  }
  return null;
};

export const validateLogic = (logic: StyleRuleLogic, fields: MapComponentFilterField[]): string | null => {
  const validateNode = (node: StyleRuleLogic): string | null => {
    if (node.type === 'condition') {
      if (node.operatorId === 'match_everything') {
        return null;
      }
      const field = fields.find(entry => entry.id === node.fieldId);
      if (!field) {
        return 'Select a field for each condition.';
      }
      if (!node.operatorId) {
        return 'Select an operator for each condition.';
      }
      const operator = getOperatorById(field, node.operatorId);
      const inputType = getOperatorInput(field, operator?.id ?? node.operatorId);
      if (inputType !== 'none' && (!node.values || node.values.length === 0 || node.values.every(value => !value.trim()))) {
        return 'Provide at least one value for each condition.';
      }
      return null;
    }
    for (const child of node.children) {
      const result = validateNode(child);
      if (result) {
        return result;
      }
    }
    return null;
  };

  return validateNode(logic);
};

export const sanitizeLogic = (logic: StyleRuleLogic): StyleRuleLogic | null => {
  if (!logic) {
    return null;
  }
  const sanitize = (node: any): StyleRuleLogic | null => {
    if (!node) {
      return null;
    }
    if (node.type === 'condition') {
      return {
        id: node.id ?? randomId('condition'),
        type: 'condition',
        fieldId: String(node.fieldId ?? ''),
        operatorId: String(node.operatorId ?? 'eq'),
        values: Array.isArray(node.values) ? node.values.map((value: any) => String(value)) : [],
      };
    }
    if (node.type === 'group') {
      const children = Array.isArray(node.children) ? node.children.map(sanitize).filter(Boolean) as StyleRuleLogic[] : [];
      return {
        id: node.id ?? randomId('group'),
        type: 'group',
        operator: node.operator === 'OR' ? 'OR' : 'AND',
        children,
      };
    }
    return null;
  };

  return sanitize(logic);
};
